import * as React from 'react'
import { AIIcon } from '~/components/icons/ai'
import { LanternInput } from '~/components/mkt/lantern-input'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import LoadingIcon from './loading-icon'
import { gsap } from 'gsap'
import { clsx } from 'clsx'
import { toast } from 'sonner'
import { ThumbsUpIcon } from '~/components/icons/thumbs-up'
import { ThumbsDownIcon } from '~/components/icons/thumbs-down'
import ph from 'posthog-js'
import {
  useSchemaAssistant,
  type SchemaAssistantReturnType,
} from '~/app/api/ai/schema-assistant/client'
import { Button } from '~/sotano-kit/primitives/button'
import { useUploadFileToRepoMutation } from '~/hooks-data/use-upload-file-to-repo-mutation'
import type { SchemaCompletionUserProps } from '~/app/api/ai/schema-assistant/server'
import { CrossCircledIcon } from '@radix-ui/react-icons'
import { mergeRefs } from '~/lib/merge-refs'
import { run } from '@basehub/utils'
import { VALID_IMAGE_MIME_TYPES } from '@basehub/data/replicache/schema/block/valid-mime-types'
import { useEditorStore } from '../../editor-store'

type AIModelingInputProps = {
  path: string
  onExitAiModeling?: () => void
  onStatusChange?: (status: SchemaAssistantReturnType['status']) => void
  placeholder?: string
  submitButtonClassName?: string
  autoFocus?: boolean
} & (
  | { mode: 'create-document' }
  | {
      mode: 'continue'
      blockId: string
      posIdx: number
      currentBlocks: { id: string; title: string; type: string }[]
    }
  | {
      mode: 'fill-document'
      path: string
      documentTitle: string
      onExitAiModeling?: () => void
      onStatusChange?: (status: SchemaAssistantReturnType['status']) => void
      placeholder?: string
      submitButtonClassName?: string
      autoFocus?: boolean
    }
)

export const AIModelingInput = React.forwardRef<
  HTMLInputElement,
  AIModelingInputProps
>(
  (
    {
      onStatusChange,
      autoFocus,
      placeholder,
      submitButtonClassName,
      onExitAiModeling,
      ...props
    },
    ref
  ) => {
    const { mutate, status: schemaAssistantStatus } = useSchemaAssistant()
    const { mutateAsync: uploadFile, status: uploadFileStatus } =
      useUploadFileToRepoMutation()
    const status =
      schemaAssistantStatus === 'loading' || uploadFileStatus === 'loading'
        ? 'loading'
        : schemaAssistantStatus === 'error' || uploadFileStatus === 'error'
          ? 'error'
          : schemaAssistantStatus === 'success' ||
              uploadFileStatus === 'success'
            ? 'success'
            : 'idle'

    const [replicacheError, setReplicacheError] = React.useState(false)
    const isError = status === 'error' || replicacheError
    const inputRef = React.useRef<HTMLInputElement>(null)
    const imageRef = React.useRef<HTMLInputElement>(null)

    const { data } = useRepoReplicache()
    const replicache = data?.replicache
    const repoId = data?.repoId
    const teamId = data?.team.id

    const { data: selectedTeamAndRepo } = useSelectedTeamAndRepo()

    const aiGeneration = useEditorStore((s) => s.aiGeneration)
    const setAiGeneration = useEditorStore((s) => s.setAiGeneration)
    const formRef = React.useRef<HTMLFormElement>(null)
    const formLoadingRef = React.useRef<HTMLDivElement>(null)

    const [submitButtonEnabled, setSubmitButtonEnabled] = React.useState(false)
    // animates input exiting
    React.useEffect(() => {
      if (aiGeneration?.status !== 'input-exiting' || !formRef.current) return
      const tween = gsap.to(formRef.current, {
        delay: 1,
        duration: 0.2,
        opacity: 0,
        y: 5,
        onComplete: () => {
          setAiGeneration({
            status: 'blocks-entering',
            generatedBlockIds: aiGeneration.generatedBlockIds,
          })
        },
      })

      return () => {
        tween.revert()
      }
    }, [aiGeneration, setAiGeneration])

    React.useEffect(() => {
      if (!onStatusChange) return

      onStatusChange(status)
    }, [status, onStatusChange])

    // odd bug when pressing Enter to open this input
    React.useEffect(() => {
      setSubmitButtonEnabled(true)
    }, [])

    const [imagePreviewURL, setImagePreviewURL] = React.useState<string>()
    const [isDroppingImage, setIsDroppingImage] = React.useState(false)

    const handleFileInputChange = React.useCallback(
      (file: File) => {
        setImagePreviewURL(URL.createObjectURL(file))
        if (inputRef.current && !inputRef.current.value) {
          // prefill it
          inputRef.current.value = run(() => {
            switch (props.mode) {
              case 'fill-document':
                return `schema for "${props.documentTitle}" with this image`
              case 'create-document':
                return `schema for this image`
              case 'continue':
                return `continue building the schema based on this image`
              default:
                return ''
            }
          })
          inputRef.current.focus()
        }
      },
      [props]
    )

    return (
      <form
        className="relative"
        ref={formRef}
        onSubmit={async (e) => {
          e.preventDefault()

          if (!teamId || !repoId) return

          const formData = new FormData(e.currentTarget)
          const prompt = formData.get('prompt') as string
          const file = formData.get('image') as File | null

          let imageUrl: string | null = null
          if (file && VALID_IMAGE_MIME_TYPES.includes(file?.type)) {
            const result = await uploadFile({ file, repoId, teamId })
            imageUrl = result.uploadURL
          }

          let genProps: SchemaCompletionUserProps | null = null
          switch (props.mode) {
            case 'create-document':
            case 'fill-document':
              if (imageUrl) {
                genProps = { type: 'new-vision', prompt, imageUrl }
              } else {
                genProps = { type: 'new', prompt }
              }
              break
            case 'continue':
              if (imageUrl) {
                genProps = {
                  type: 'continue-vision',
                  prompt,
                  imageUrl,
                  posIdx: props.posIdx,
                  currentBlocks: props.currentBlocks,
                }
              } else {
                genProps = {
                  type: 'continue',
                  prompt,
                  posIdx: props.posIdx,
                  currentBlocks: props.currentBlocks,
                }
              }
              break
            default:
              throw new Error('Invalid mode')
          }

          mutate(genProps, {
            onSuccess: async (data) => {
              if (status === 'loading' || !replicache) return
              setAiGeneration({
                status: 'input-exiting',
                generatedBlockIds: [],
              })

              try {
                setReplicacheError(false)

                // const blockIds =
                //   await replicache.mutate.createBlocksFromStructuredCompletion({
                //     structuredCompletionBlocks: data.output.blocks,
                //     availableComponents: data.availableComponents,
                //     idSeed: getId(),
                //     isoNow: new Date().toISOString(),
                //     path,
                //     ...(props.mode === 'continue'
                //       ? { options: { _afterBlockId: props.blockId } }
                //       : {}),
                //   })

                setTimeout(() => {
                  const t = toast(
                    <div className="flex items-center justify-between flex-1 w-full gap-3">
                      <div>Was this helpful?</div>
                      <div className="flex items-center p-2 space-x-2">
                        <button
                          onClick={async () => {
                            ph.capture('feedback', {
                              feedback: 'positive',
                              type: 'structured-completion',
                            })
                            toast.dismiss(t)
                          }}
                          className="cursor-default"
                          aria-label="thumbs up"
                        >
                          <ThumbsUpIcon />
                        </button>
                        <button
                          onClick={async () => {
                            ph.capture('feedback', {
                              feedback: 'negative',
                              type: 'structured-completion',
                            })
                            toast.dismiss(t)
                          }}
                          className="cursor-default"
                          aria-label="thumbs down"
                        >
                          <ThumbsDownIcon />
                        </button>
                      </div>
                    </div>,
                    {
                      duration: 15 * 1000, // 15 seconds,
                    }
                  )
                }, 400 * data.output.blocks.length)

                // setAiGeneration({
                //   status: 'input-exiting',
                //   generatedBlockIds: blockIds,
                // })

                // const [rootBlockId] = blockIds

                // if (!rootBlockId) return

                // if (props.mode === 'create-document') {
                //   addTab({
                //     key: rootBlockId,
                //     partiallyOpen: false,
                //   })
                // }
              } catch (error) {
                toast.error(
                  "Something's wrong generating blocks. please try again with a different prompt."
                )
                console.error(error)
                setReplicacheError(true)
                ph.capture('Structured Completion Error', {
                  teamId: selectedTeamAndRepo?.team?.id,
                  repoId: selectedTeamAndRepo?.repo?.id,
                  error,
                })
              }
            },
            onError: async (error: unknown) => {
              console.error(error)
              toast.error(
                typeof error === 'object' &&
                  error &&
                  'error' in error &&
                  typeof error.error === 'string'
                  ? error.error
                  : "Something's wrong, please try again."
              )
            },
          })
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDroppingImage(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setIsDroppingImage(false)
        }}
      >
        <div
          className={clsx(
            'transition-opacity duration-1000 ease-in',
            status === 'loading' ? 'opacity-50' : 'opacity-0'
          )}
        >
          <div
            ref={formLoadingRef}
            className={clsx(
              'bg-dark-control-base pointer-events-none absolute top-1/2 left-1/2 h-[140%] w-[110%] -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-[50%] blur-2xl z-0',
              status === 'loading' && 'animate-pulse'
            )}
          />
        </div>
        <AIIcon className="absolute z-50 -translate-y-1/2 pointer-events-none top-1/2 left-3" />
        <LanternInput
          ref={mergeRefs([ref, inputRef])}
          inputProps={{
            placeholder: placeholder ?? 'What do you want to build?',
            className: clsx(
              'px-[34px] !rounded-lg !h-11',
              imagePreviewURL ? 'pr-[188px]' : 'pr-[140px]'
            ),
            name: 'prompt',
            defaultValue: run(() => {
              switch (props.mode) {
                case 'fill-document':
                  return `schema for "${props.documentTitle}"`
                case 'create-document':
                  return ``
                case 'continue':
                  return `continue building the schema`
                default:
                  break
              }
            }),
            onKeyDown: (e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onExitAiModeling?.()
              }
              // if press backspace and input is empty, exit ai modeling
              if (e.key === 'Backspace' && e.currentTarget.value === '') {
                e.preventDefault()
                onExitAiModeling?.()
              }
            },
            required: true,
            autoComplete: 'off',
            autoFocus,
          }}
          className="!h-11 rounded-lg z-40"
        />
        <div
          className={clsx(
            'absolute z-50 -translate-y-1/2 top-1/2 right-3 flex items-center gap-2'
          )}
        >
          {imagePreviewURL && (
            <div className="relative">
              <button
                className="absolute -top-1 -right-1 text-dark-muted hover:text-dark-dim bg-dark-bg rounded-full cursor-default"
                type="button"
                onClick={() => {
                  setImagePreviewURL(undefined)
                  if (imageRef.current) imageRef.current.value = ''
                }}
              >
                <CrossCircledIcon />
              </button>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imagePreviewURL}
                alt=""
                width={32}
                height={32}
                className="rounded-lg border border-dark-border-solid w-[32px] h-8 object-cover"
              />
            </div>
          )}
          <Button
            type="button"
            unstyled
            onClick={() => {
              imageRef.current?.click()
            }}
            className={clsx(
              'flex items-center gap-1 text-dark-muted text-xs cursor-default py-0.5 px-1.5 hover:bg-dark-border-solid hover:text-dark-dim transition-colors rounded-lg',
              status === 'loading' && 'hover:!bg-transparent'
            )}
            disabled={!submitButtonEnabled}
          >
            Upload Image
          </Button>
          <input
            ref={imageRef}
            type="file"
            name="image"
            accept={VALID_IMAGE_MIME_TYPES.join(',')}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (!file) return
              handleFileInputChange(file)
            }}
            hidden
          />
          <IconButton
            className={clsx(
              status === 'loading' && 'hover:!bg-transparent',
              submitButtonClassName
            )}
            disabled={!submitButtonEnabled}
          >
            <LoadingIcon status={isError ? 'error' : status} />
          </IconButton>
        </div>
        {isDroppingImage && (
          <div
            className="absolute inset-0 z-50 bg-dark-base select-none border border-dashed border-dark-elevated text-dark-dim text-xs flex items-center justify-center rounded-lg"
            onDrop={(e) => {
              e.preventDefault()
              const file = e.dataTransfer.files?.[0]
              if (
                file &&
                VALID_IMAGE_MIME_TYPES.includes(file.type) &&
                imageRef.current
              ) {
                handleFileInputChange(file)
                imageRef.current.files = e.dataTransfer.files
              }
              setIsDroppingImage(false)
            }}
          >
            Drop image.
          </div>
        )}
      </form>
    )
  }
)
